<template>
  <div class="chatbot-widget">
    <!-- Chat Toggle Button -->
    <button v-if="!isOpen" @click="toggleChat" class="chat-toggle-btn-image">
      <img
        src="/happyalien.png"
        alt="Chat"
        class="w-14 h-14 object-contain drop-shadow-lg"
      />
    </button>
    <button v-else @click="toggleChat" class="chat-toggle-btn-close">
      <PhX :size="24" />
    </button>

    <!-- Chat Window -->
    <Transition name="chat-window">
      <div v-if="isOpen" class="chat-window">
        <!-- Header -->
        <div class="chat-header">
          <div class="flex items-center space-x-2">
            <img
              src="/happyalien.png"
              alt="Happy Alien"
              class="w-10 h-10 object-contain"
            />
            <div>
              <h3 class="font-semibold text-white">Happy Alien Support</h3>
              <p class="text-xs text-white/80">Ask me anything!</p>
            </div>
          </div>
          <button @click="toggleChat" class="text-white/80 hover:text-white">
            <PhX :size="20" />
          </button>
        </div>

        <!-- Messages Area -->
        <div ref="messagesContainer" class="chat-messages">
          <!-- Welcome message -->
          <div v-if="messages.length === 0" class="welcome-message">
            <img
              src="/happyalien.png"
              alt="Happy Alien"
              class="w-16 h-16 mx-auto mb-2"
            />
            <h4 class="font-semibold text-gray-800">Welcome to Happy Alien!</h4>
            <p class="text-sm text-gray-600 mt-1">
              I can help you learn about our products, answer questions, or
              connect you with our team.
            </p>
            <div class="mt-3 flex flex-wrap gap-2 justify-center">
              <button
                v-for="suggestion in suggestions"
                :key="suggestion"
                @click="sendMessage(suggestion)"
                class="suggestion-chip"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>

          <!-- Chat Messages -->
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="message.role === 'user' ? 'message-user' : 'message-bot'"
          >
            <div
              class="message-bubble"
              v-html="formatMessage(message.content)"
            ></div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isLoading" class="message message-bot">
            <div class="message-bubble typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="chat-input">
          <input
            v-model="inputMessage"
            @keyup.enter="sendMessage()"
            type="text"
            placeholder="Type your message..."
            :disabled="isLoading"
            class="chat-input-field"
          />
          <button
            @click="sendMessage()"
            :disabled="!inputMessage.trim() || isLoading"
            class="chat-send-btn"
          >
            <PhPaperPlaneRight :size="20" />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted } from "vue";
import { PhX, PhPaperPlaneRight } from "@phosphor-icons/vue";
import { marked } from "marked";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const isOpen = ref(false);
const inputMessage = ref("");
const messages = ref<Message[]>([]);
const isLoading = ref(false);
const messagesContainer = ref<HTMLElement | null>(null);
const conversationId = ref(crypto.randomUUID());

const suggestions = [
  "What is VidSabio?",
  "Tell me about your products",
  "I want a demo",
];

// API URL from environment - use Happy Alien API for chatbot (sparks API hosts the chatbot)
const apiUrl = (import.meta as any).env.VITE_HAPPY_ALIEN_API_URL || "http://localhost:3001";

function toggleChat() {
  isOpen.value = !isOpen.value;
}

function formatMessage(content: string): string {
  // Parse markdown to HTML
  return marked.parse(content, { breaks: true }) as string;
}

async function scrollToBottom() {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

async function sendMessage(text?: string) {
  const messageText = text || inputMessage.value.trim();
  if (!messageText) return;

  // Add user message
  messages.value.push({
    role: "user",
    content: messageText,
  });
  inputMessage.value = "";
  isLoading.value = true;
  await scrollToBottom();

  try {
    // Build conversation history for context
    const history = messages.value.slice(0, -1).map((m) => ({
      role: m.role === "user" ? "user" : "model",
      parts: [m.content],
    }));

    const response = await fetch(`${apiUrl}/api/chatbot/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: messageText,
        conversationId: conversationId.value,
        history,
      }),
    });

    const data = await response.json();

    if (data.success) {
      messages.value.push({
        role: "assistant",
        content: data.reply,
      });
    } else {
      messages.value.push({
        role: "assistant",
        content:
          "Sorry, I encountered an error. Please try again or contact support@happyalien.ai",
      });
    }
  } catch (error) {
    console.error("Chatbot error:", error);
    messages.value.push({
      role: "assistant",
      content:
        "Sorry, I couldn't connect to the server. Please check your connection and try again.",
    });
  } finally {
    isLoading.value = false;
    await scrollToBottom();
  }
}

onMounted(() => {
  // Generate a unique conversation ID for this session
  conversationId.value = crypto.randomUUID();
});
</script>

<style scoped>
.chatbot-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
}

.chat-toggle-btn-image {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.3s ease;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.chat-toggle-btn-image:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4));
}

.chat-toggle-btn-close {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #6b7280;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.chat-toggle-btn-close:hover {
  transform: scale(1.05);
  background: #4b5563;
}

.chat-window {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 380px;
  height: 520px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  background: #1f2937;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.welcome-message {
  text-align: center;
  padding: 24px 16px;
}

.suggestion-chip {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
}

.suggestion-chip:hover {
  background: #63d471;
  color: white;
  border-color: #63d471;
}

.message {
  display: flex;
  max-width: 85%;
}

.message-user {
  align-self: flex-end;
  justify-content: flex-end;
}

.message-bot {
  align-self: flex-start;
}

.message-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.4;
}

.message-user .message-bubble {
  background: #39ff14;
  color: #0f172a;
  border-bottom-right-radius: 4px;
}

.message-bot .message-bubble {
  background: #f3f4f6;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}

/* Markdown styling in messages */
.message-bubble :deep(p) {
  margin: 0 0 8px 0;
}

.message-bubble :deep(p:last-child) {
  margin-bottom: 0;
}

.message-bubble :deep(ul),
.message-bubble :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.message-bubble :deep(li) {
  margin: 4px 0;
}

.message-bubble :deep(strong) {
  font-weight: 600;
}

.message-bubble :deep(code) {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #9ca3af;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-8px);
  }
}

.chat-input {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
}

.chat-input-field {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.chat-input-field:focus {
  border-color: #63d471;
}

.chat-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #63d471;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.chat-send-btn:hover:not(:disabled) {
  background: #4ade80;
}

.chat-send-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

/* Animations */
.chat-window-enter-active,
.chat-window-leave-active {
  transition: all 0.3s ease;
}

.chat-window-enter-from,
.chat-window-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* Mobile responsiveness */
@media (max-width: 440px) {
  .chat-window {
    width: calc(100vw - 32px);
    right: -8px;
    height: 70vh;
    max-height: 520px;
  }
}
</style>
